import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Inline, Badge, useResponsiveValue, Text, HiddenVisually, Hidden, IconRenderer, TooltipRenderer, IconPositive, ButtonIcon, IconBookmark, Columns, Column, IconChevron, Stack, Link, List } from "braid-design-system";
import { createContext, useContext, memo, useState, forwardRef, useRef } from "react";
import { translations } from "../.vocab/index.mjs";
import { splitView, tappableJob, outlinedJob, trackingPixel, markVisited, showVisited, showHovered } from "./JobCard.css.mjs";
import { DotSeparator } from "./components/DotSeparator/DotSeparator.mjs";
import { MetaLink } from "./components/MetaLink/MetaLink.mjs";
import { translations as translations$1 } from "../components/SaveJobForSignedIn/.vocab/index.mjs";
import { translations as translations$2 } from "../components/SaveJobForSignedOut/.vocab/index.mjs";
import { translations as translations$3 } from "../components/JobCardFooter/.vocab/index.mjs";
import { LazyLoadedImg } from "./components/LazyLoadedImg/LazyLoadedImg.mjs";
import { getJobDataAutomation, generateJobTitleLinkId, highlightFromHTML } from "./utils/helpers.mjs";
const ClickThroughOverlay = ({ children }) => /* @__PURE__ */ jsx(Box, { position: { desktop: "relative" }, display: "inline", children });
const initialState = {};
const JobCardContext = createContext(initialState);
const useJobCardContext = () => {
  const {
    companyLink,
    classificationLinks,
    id,
    isAuthenticated,
    isPremium = false,
    isSelected = false,
    jobMetaLinkOnClick,
    srcLogo,
    saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
    tags,
    featureFlags,
    bulletPoints,
    isViewed = false,
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
    jobLinkOnClick,
    jobTitle: title,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationLinks,
    locationMatch,
    listPosition,
    salaryDisplayLabel,
    shouldAddHoverEffect = false,
    shouldAddFocusEffect = false,
    onClick,
    teaser,
    workType,
    workArrangement,
    ...result
  } = useContext(JobCardContext);
  return {
    ...result,
    companyLink,
    classificationLinks,
    id,
    isAuthenticated,
    isPremium,
    isSelected,
    jobMetaLinkOnClick,
    srcLogo,
    saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
    tags,
    featureFlags,
    bulletPoints,
    isViewed,
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
    jobLinkOnClick,
    jobTitle: title,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationLinks,
    locationMatch,
    listPosition,
    salaryDisplayLabel,
    shouldAddHoverEffect,
    shouldAddFocusEffect,
    onClick,
    teaser,
    workType,
    workArrangement
  };
};
const JobCardContextProvider = ({
  children,
  ...props
}) => /* @__PURE__ */ jsx(JobCardContext.Provider, { value: { ...props }, children });
const premiumAdBadgeAutomation = "premiumAdBadge";
const JobBadge = ({ label, tone, automation }) => /* @__PURE__ */ jsx(Box, { pointerEvents: "none", children: /* @__PURE__ */ jsx(Badge, { tone, data: { automation }, children: label }) });
const useJobBadges = () => {
  var _a;
  const { t } = useTranslations(translations);
  const {
    tags,
    isPremium,
    featureFlags,
    saveJob: { isApplied }
  } = useJobCardContext();
  const behaviouralCuesEnabled = featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled;
  const isForNewJobCardDensityExperiment = featureFlags == null ? void 0 : featureFlags.newJobCardDensity;
  const newToYouBadgeLabel = behaviouralCuesEnabled && ((_a = tags == null ? void 0 : tags.find(({ type }) => type === "NEW" || type === "UNSEEN")) == null ? void 0 : _a.label);
  const newToYouBadge = newToYouBadgeLabel ? [
    {
      label: newToYouBadgeLabel,
      tone: "positive",
      automation: "newToYouAdBadge"
    }
  ] : [];
  const badges = !isForNewJobCardDensityExperiment && isPremium ? [
    {
      label: t("Featured"),
      tone: "promote",
      automation: premiumAdBadgeAutomation
    }
  ] : newToYouBadge;
  const tagsBadges = (tags == null ? void 0 : tags.reduce((allBadges, { type, label }) => {
    if (type === "URGENT") {
      allBadges.push({ label, tone: "promote", automation: "urgentAdBadge" });
    }
    if (isForNewJobCardDensityExperiment && !isApplied) {
      if (type === "EARLY_APPLICANT") {
        allBadges.push({
          label,
          tone: "info",
          automation: "earlyApplicantAdBadge"
        });
      }
      if (type === "EXPIRES_SOON") {
        allBadges.push({
          label,
          tone: "critical",
          automation: "expiresSoonAdBadge"
        });
      }
    }
    return allBadges;
  }, [])) ?? [];
  return badges.concat(tagsBadges);
};
const JobBadges = ({ badges }) => badges.length ? /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: badges.map(({ automation, label, tone }, index) => /* @__PURE__ */ jsx(
  JobBadge,
  {
    label,
    tone,
    automation
  },
  index
)) }) : null;
const useViewStatus = ({
  isPremium,
  tags
}) => {
  if (isPremium || !(tags && tags.length > 0)) return;
  const mappedTags = /* @__PURE__ */ new Map();
  tags.map((t) => {
    mappedTags.set(t.type, t.label);
  });
  return mappedTags.get("APP_FINISHED") || mappedTags.get("APP_STARTED") || mappedTags.get("VIEWED");
};
const ClassificationLink = ({
  link,
  type,
  isChild = false
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(HiddenVisually, { children: `${type}: ${link.name}` }),
  /* @__PURE__ */ jsx(
    MetaLink,
    {
      name: link.name,
      href: link.href,
      rel: link.rel,
      target: link.target,
      title: link.title,
      type,
      isChild,
      disabledLink: true
    }
  )
] });
const JobClassificationsWrapper = ({ children, isForNewJobCardDensityExperiment }) => {
  if (isForNewJobCardDensityExperiment) {
    return children;
  }
  return /* @__PURE__ */ jsx(Hidden, { below: "tablet", children });
};
const JobClassifications = () => {
  const { classificationLinks: link, featureFlags } = useJobCardContext();
  const [classification, subClassification] = link;
  const newJobCardDensity = Boolean(featureFlags == null ? void 0 : featureFlags.newJobCardDensity);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  return /* @__PURE__ */ jsx(
    JobClassificationsWrapper,
    {
      isForNewJobCardDensityExperiment: newJobCardDensity,
      children: /* @__PURE__ */ jsx(
        Text,
        {
          size: newJobCardDensity && isMobile ? "xsmall" : "small",
          tone: newJobCardDensity ? "secondary" : void 0,
          children: /* @__PURE__ */ jsxs(Inline, { space: "none", children: [
            subClassification && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(
                ClassificationLink,
                {
                  link: subClassification,
                  type: "subClassification"
                }
              ),
              /* @__PURE__ */ jsx(Box, { marginLeft: "xxsmall" })
            ] }),
            /* @__PURE__ */ jsx(
              ClassificationLink,
              {
                link: classification,
                type: "classification",
                isChild: Boolean(subClassification)
              }
            )
          ] })
        }
      )
    }
  );
};
const ClockIcon = () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "currentColor",
    className,
    "aria-hidden": true,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M1 6C1 3.22727 3.22727 1 6 1C8.77273 1 11 3.22727 11 6C11 8.77273 8.77273 11 6 11C3.22727 11 1 8.77273 1 6ZM6.45455 5.72727L8 6.5C8.22727 6.63636 8.31818 6.90909 8.22727 7.09091C8.13636 7.27273 8 7.36364 7.81818 7.36364C7.72727 7.36364 7.68182 7.36364 7.63636 7.31818L5.81818 6.40909C5.63636 6.31818 5.54545 6.18182 5.54545 6V3.27273C5.54545 3 5.72727 2.81818 6 2.81818C6.27273 2.81818 6.45455 3 6.45455 3.27273V5.72727Z"
      }
    )
  }
) }) });
const HourglassIcon = () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "currentColor",
    className,
    "aria-hidden": true,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M2.5 1C2.22386 1 2 1.22386 2 1.5C2 1.77614 2.22386 2 2.5 2H3V3.5C3 4.54349 3.53275 5.46254 4.3411 6C3.53275 6.53746 3 7.45651 3 8.5V10H2.5C2.22386 10 2 10.2239 2 10.5C2 10.7761 2.22386 11 2.5 11H9.5C9.77614 11 10 10.7761 10 10.5C10 10.2239 9.77614 10 9.5 10H9V8.5C9 7.45651 8.46725 6.53746 7.6589 6C8.46725 5.46254 9 4.54349 9 3.5V2H9.5C9.77614 2 10 1.77614 10 1.5C10 1.22386 9.77614 1 9.5 1H2.5ZM6 5.5C7.10457 5.5 8 4.60457 8 3.5V2H4V3.5C4 4.60457 4.89543 5.5 6 5.5Z"
      }
    )
  }
) }) });
const useJobState = (tags) => {
  if (!tags) return;
  const jobStateTags = /* @__PURE__ */ new Map();
  tags.map((tag) => jobStateTags.set(tag.type, tag));
  return jobStateTags.get("EXPIRES_SOON") || jobStateTags.get("EARLY_APPLICANT");
};
const JobState = ({ jobStateTag }) => {
  switch (jobStateTag == null ? void 0 : jobStateTag.type) {
    case "EARLY_APPLICANT":
      return /* @__PURE__ */ jsx(EarlyApplicant, { label: jobStateTag.label });
    case "EXPIRES_SOON":
      return /* @__PURE__ */ jsx(ExpiresSoon, { label: jobStateTag.label });
    case void 0:
      return null;
  }
};
const EarlyApplicant = ({ label }) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { tone: "info", size: "small", weight: "strong", icon: /* @__PURE__ */ jsx(ClockIcon, {}), children: label }) });
const ExpiresSoon = ({ label }) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { tone: "critical", size: "small", weight: "strong", icon: /* @__PURE__ */ jsx(HourglassIcon, {}), children: label }) });
const SavedJobForApplied = () => {
  const { t } = useTranslations(translations$1);
  const title = t("You have applied for this job");
  const { id } = useJobCardContext();
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: `serp_applied_${jobId}`,
      tooltip: /* @__PURE__ */ jsx(Text, { children: title }),
      children: ({ triggerProps }) => /* @__PURE__ */ jsx(
        Box,
        {
          ...triggerProps,
          data: {
            testid: "applied-job-icon",
            automation: "applied-job"
          },
          children: /* @__PURE__ */ jsx(IconPositive, { tone: "positive" })
        }
      )
    }
  );
};
const SaveJobButton = () => {
  const { t } = useTranslations(translations$1);
  const {
    id,
    saveJob: { isSaved, toggleSavedJob }
  } = useJobCardContext();
  const buttonText = isSaved ? t("Unsave") : t("Save");
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-save-job-${jobId}`,
      label: buttonText,
      variant: "transparent",
      data: {
        testid: isSaved ? "remove-save-job" : "save-job",
        automation: isSaved ? "remove-saved-job" : "save-job"
      },
      icon: /* @__PURE__ */ jsx(
        IconBookmark,
        {
          tone: isSaved ? "brandAccent" : void 0,
          active: isSaved
        }
      ),
      onClick: (e) => {
        e.stopPropagation();
        toggleSavedJob(e);
      }
    }
  );
};
const SaveJobForSignedIn = () => {
  const {
    saveJob: { isApplied }
  } = useJobCardContext();
  return isApplied ? /* @__PURE__ */ jsx(SavedJobForApplied, {}) : /* @__PURE__ */ jsx(SaveJobButton, {});
};
const SaveJobForSignedOut = () => {
  const { t } = useTranslations(translations$2);
  const {
    id,
    saveJob: { toggleSavedJob }
  } = useJobCardContext();
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-signed-out-save-job-${jobId}`,
      label: t("Sign in to save this job"),
      variant: "transparent",
      data: {
        testid: "signed-out-save-job",
        automation: "signed-out-save-job"
      },
      icon: /* @__PURE__ */ jsx(IconBookmark, {}),
      onClick: toggleSavedJob
    }
  );
};
const SaveJob = () => {
  const { isAuthenticated } = useJobCardContext();
  return isAuthenticated ? /* @__PURE__ */ jsx(SaveJobForSignedIn, {}) : /* @__PURE__ */ jsx(SaveJobForSignedOut, {});
};
const SaveJob$1 = memo(SaveJob);
const jobListingDateAutomation = "jobListingDate";
const Classifications = ({
  isClassificationVisible
}) => {
  const { classificationLinks: link } = useJobCardContext();
  if (link.length === 0) {
    return null;
  }
  if (isClassificationVisible) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        marginBottom: "medium",
        data: { testid: "job-classification-visible" },
        children: /* @__PURE__ */ jsx(JobClassifications, {})
      }
    );
  }
  return /* @__PURE__ */ jsx(HiddenVisually, { data: { testid: "job-classification-hidden" }, children: /* @__PURE__ */ jsx(JobClassifications, {}) });
};
const JobCardFooter = () => {
  const {
    featureFlags,
    isPremium,
    listingDate,
    tags,
    id,
    saveJob: { isApplied },
    onToggleContent
  } = useJobCardContext();
  const { t } = useTranslations(translations$3);
  const viewStatus = useViewStatus({ isPremium, tags });
  const jobState = useJobState(tags);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  const [isClassificationVisible, setClassificationVisibility] = useState(false);
  if (featureFlags == null ? void 0 : featureFlags.newJobCardDensity) {
    const getFirstColumnContent = () => {
      if (isPremium) {
        return /* @__PURE__ */ jsx(Text, { size: isMobile ? "xsmall" : "small", tone: "secondary", children: t("Featured") });
      }
      if (listingDate) {
        return /* @__PURE__ */ jsx(
          Text,
          {
            size: isMobile ? "xsmall" : "small",
            tone: "secondary",
            data: { automation: jobListingDateAutomation },
            children: (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && viewStatus ? /* @__PURE__ */ jsx(DotSeparator, { content: [listingDate, viewStatus] }) : listingDate
          }
        );
      }
      return null;
    };
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Classifications, { isClassificationVisible }),
      /* @__PURE__ */ jsxs(Columns, { space: "none", alignY: "center", children: [
        /* @__PURE__ */ jsx(Column, { children: getFirstColumnContent() }),
        /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(Inline, { space: "large", align: "right", alignY: "center", children: [
          /* @__PURE__ */ jsx(
            ButtonIcon,
            {
              icon: /* @__PURE__ */ jsx(
                IconChevron,
                {
                  direction: isClassificationVisible ? "up" : "down"
                }
              ),
              label: isClassificationVisible ? t("Less") : t("More"),
              variant: "transparent",
              id: `job-card-footer-expand-collapse-${id}`,
              onClick: (e) => {
                e.stopPropagation();
                onToggleContent == null ? void 0 : onToggleContent(
                  isClassificationVisible ? "collapse" : "expand"
                );
                setClassificationVisibility(!isClassificationVisible);
              }
            }
          ),
          /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(SaveJob$1, {}) })
        ] }) })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    !isPremium && listingDate ? /* @__PURE__ */ jsx(
      Text,
      {
        size: "small",
        tone: "secondary",
        data: { automation: jobListingDateAutomation },
        children: (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && viewStatus ? /* @__PURE__ */ jsx(DotSeparator, { content: [listingDate, viewStatus] }) : listingDate
      }
    ) : null,
    !isApplied && jobState && /* @__PURE__ */ jsx(JobState, { jobStateTag: jobState })
  ] });
};
const JobCardBase = forwardRef(
  ({ "aria-label": ariaLabel }, ref) => {
    const {
      companyLink,
      id,
      isPremium,
      isSelected,
      srcLogo,
      tags,
      featureFlags,
      bulletPoints,
      isViewed,
      jobDetailPageLink,
      jobDetailPageJobCardOriginLink,
      jobDetailPageCardTitleOriginLink,
      jobLinkOnClick,
      jobTitle: title,
      joraImpressionTrackingUrl,
      listingDateDescription,
      locationMatch,
      listPosition,
      shouldAddHoverEffect,
      shouldAddFocusEffect,
      onClick,
      teaser
    } = useJobCardContext();
    const { t } = useTranslations(translations);
    const badges = useJobBadges();
    const shouldRenderLogo = srcLogo !== void 0;
    const isForNewJobCardDensityExperiment = featureFlags == null ? void 0 : featureFlags.newJobCardDensity;
    const responsiveValue = useResponsiveValue();
    const shouldRenderBadges = Boolean(badges == null ? void 0 : badges.length) || isPremium;
    const isMobileAndTablet = responsiveValue({
      mobile: true,
      desktop: false
    });
    const isMobile = responsiveValue({
      mobile: true,
      tablet: false
    });
    const showJobCardTeaser = !isForNewJobCardDensityExperiment && !(featureFlags == null ? void 0 : featureFlags.jobCardDensityExperiment1HideTeaser) && ((featureFlags == null ? void 0 : featureFlags.isShowJobCardTeaserInSplitView) || isMobileAndTablet);
    const premiumBadgeDescriptionText = t("This is a featured job");
    const isContainViewedTag = (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && (tags == null ? void 0 : tags.some(({ type }) => type === "VIEWED"));
    return /* @__PURE__ */ jsxs(
      Box,
      {
        ...listPosition && { id: `jobcard-${listPosition}` },
        component: "article",
        cursor: "pointer",
        className: [
          splitView.jobCardBase,
          {
            [splitView.selectedJobCard]: isSelected,
            [splitView.hoveredJobCard]: shouldAddHoverEffect,
            [splitView.focusedJobCard]: shouldAddFocusEffect
          }
        ],
        padding: {
          mobile: isForNewJobCardDensityExperiment ? "medium" : "gutter",
          tablet: "gutter"
        },
        background: "surface",
        borderRadius: "large",
        "aria-label": ariaLabel,
        "aria-selected": isSelected,
        data: {
          automation: getJobDataAutomation(isPremium, locationMatch),
          testid: "job-card",
          "job-id": id,
          "card-type": "JobCard"
        },
        position: "relative",
        onClick,
        ref,
        children: [
          /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsx(
            Link,
            {
              href: jobDetailPageLink.href || "",
              title: jobDetailPageLink.title,
              rel: jobDetailPageLink.rel,
              className: tappableJob,
              data: {
                automation: "job-list-view-job-link"
              },
              target: (featureFlags == null ? void 0 : featureFlags.shouldOpenApplyInNewTab) ? "_blank" : "_self",
              onClick: jobLinkOnClick
            }
          ) }),
          /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(
            Link,
            {
              href: jobDetailPageJobCardOriginLink.href || "",
              title: jobDetailPageJobCardOriginLink.title,
              rel: jobDetailPageJobCardOriginLink.rel,
              className: [tappableJob, outlinedJob],
              data: {
                automation: "job-list-item-link-overlay",
                testid: "job-list-item-link-overlay",
                "run-click-only": true
              },
              onClick: jobLinkOnClick
            }
          ) }),
          joraImpressionTrackingUrl && /* @__PURE__ */ jsx(
            Box,
            {
              component: "img",
              src: joraImpressionTrackingUrl,
              className: trackingPixel,
              data: {
                testid: "jora-impression-tracking"
              }
            }
          ),
          isPremium ? /* @__PURE__ */ jsx(HiddenVisually, { children: premiumBadgeDescriptionText }) : listingDateDescription && /* @__PURE__ */ jsx(HiddenVisually, { children: listingDateDescription }),
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsxs(
              Columns,
              {
                space: "small",
                collapseBelow: isForNewJobCardDensityExperiment ? "tablet" : void 0,
                reverse: isForNewJobCardDensityExperiment,
                children: [
                  isForNewJobCardDensityExperiment && shouldRenderLogo ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
                    LazyLoadedImg,
                    {
                      src: srcLogo,
                      useNewJobCardDensityMobileStyle: Boolean(isMobile)
                    }
                  ) }) : null,
                  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(
                    Stack,
                    {
                      space: isForNewJobCardDensityExperiment || shouldRenderLogo ? "xsmall" : "medium",
                      children: [
                        !isForNewJobCardDensityExperiment && (shouldRenderLogo || shouldRenderBadges) ? /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                          shouldRenderBadges && /* @__PURE__ */ jsx(JobBadges, { badges }),
                          shouldRenderLogo && /* @__PURE__ */ jsx(LazyLoadedImg, { src: srcLogo })
                        ] }) : null,
                        /* @__PURE__ */ jsx(
                          Stack,
                          {
                            space: isForNewJobCardDensityExperiment && badges.length > 0 ? "medium" : "small",
                            children: /* @__PURE__ */ jsxs(Box, { children: [
                              /* @__PURE__ */ jsx(Text, { weight: "strong", component: "h3", children: /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(
                                Link,
                                {
                                  href: jobDetailPageCardTitleOriginLink.href || "",
                                  title: jobDetailPageCardTitleOriginLink.title,
                                  rel: jobDetailPageCardTitleOriginLink.rel,
                                  className: {
                                    // markVisited forces the color to be the visited color
                                    [markVisited]: isViewed || isContainViewedTag,
                                    // showVisited styles the :visited pseudo-class
                                    [showVisited]: !isSelected,
                                    [showHovered]: shouldAddHoverEffect
                                  },
                                  data: {
                                    automation: "jobTitle",
                                    testid: "job-card-title",
                                    "run-click-only": true
                                  },
                                  id: generateJobTitleLinkId(id),
                                  onClick: jobLinkOnClick,
                                  children: title
                                }
                              ) }) }),
                              /* @__PURE__ */ jsx(
                                Box,
                                {
                                  paddingTop: isForNewJobCardDensityExperiment ? "xsmall" : "small",
                                  children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsxs(ClickThroughOverlay, { children: [
                                    /* @__PURE__ */ jsxs(HiddenVisually, { children: [
                                      t("at"),
                                      " "
                                    ] }),
                                    /* @__PURE__ */ jsx(
                                      MetaLink,
                                      {
                                        name: companyLink.name,
                                        href: companyLink.href,
                                        rel: companyLink.rel,
                                        target: companyLink.target,
                                        title: companyLink.title,
                                        type: "company"
                                      }
                                    )
                                  ] }) })
                                }
                              ),
                              isForNewJobCardDensityExperiment && badges.length > 0 && /* @__PURE__ */ jsx(Box, { paddingTop: "small", children: /* @__PURE__ */ jsx(JobBadges, { badges }) }),
                              isForNewJobCardDensityExperiment && /* @__PURE__ */ jsx(
                                Box,
                                {
                                  paddingTop: {
                                    mobile: "small",
                                    tablet: "medium"
                                  },
                                  children: /* @__PURE__ */ jsx(JobWorkTypeLocationsSalaryAndClassifications, {})
                                }
                              )
                            ] })
                          }
                        )
                      ]
                    }
                  ) }),
                  !isForNewJobCardDensityExperiment ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(SaveJob$1, {}) }) }) : null
                ]
              }
            ),
            !isForNewJobCardDensityExperiment && /* @__PURE__ */ jsx(JobWorkTypeLocationsSalaryAndClassifications, {}),
            (bulletPoints == null ? void 0 : bulletPoints.length) ? /* @__PURE__ */ jsx(List, { size: "small", space: "xsmall", children: bulletPoints.map((bulletPoint, i) => /* @__PURE__ */ jsx(
              Text,
              {
                size: isForNewJobCardDensityExperiment && isMobile ? "xsmall" : "small",
                tone: isForNewJobCardDensityExperiment ? "secondary" : void 0,
                children: bulletPoint
              },
              i
            )) }) : null,
            showJobCardTeaser ? /* @__PURE__ */ jsx(
              Text,
              {
                size: "small",
                tone: "secondary",
                data: {
                  testid: "job-card-teaser",
                  automation: "jobShortDescription"
                },
                children: highlightFromHTML({ html: teaser, isDescription: true })
              }
            ) : null,
            /* @__PURE__ */ jsx(JobCardFooter, {})
          ] })
        ]
      }
    );
  }
);
const JobWorkTypeLocationsSalaryAndClassifications = () => {
  const {
    locationLinks,
    featureFlags,
    workArrangement,
    salaryDisplayLabel,
    workType,
    classificationLinks
  } = useJobCardContext();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    workType ? /* @__PURE__ */ jsx(HiddenVisually, { children: /* @__PURE__ */ jsx(Box, { component: "p", children: t("This is a {workType} job", { workType }) }) }) : null,
    /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Text, { size: "small", children: [
        locationLinks == null ? void 0 : locationLinks.map((props, index) => (
          // the locations will be split by comma and space
          // e.g. "Lewisham, Sydney NSW"
          /* @__PURE__ */ jsxs(
            Box,
            {
              component: "span",
              data: {
                automation: "jobCardLocation"
              },
              children: [
                index % 2 !== 0 && ", ",
                /* @__PURE__ */ jsx(
                  MetaLink,
                  {
                    name: props.name,
                    href: props.href,
                    rel: props.rel,
                    target: props.target,
                    title: props.title,
                    type: "location",
                    disabledLink: true
                  }
                )
              ]
            },
            index
          )
        )),
        (featureFlags == null ? void 0 : featureFlags.remoteSearchFilterEnabled) && workArrangement && (locationLinks == null ? void 0 : locationLinks.length) ? /* @__PURE__ */ jsxs(
          Box,
          {
            component: "span",
            marginLeft: "xxsmall",
            data: {
              testid: "work-arrangement"
            },
            children: [
              "(",
              workArrangement,
              ")"
            ]
          }
        ) : null
      ] }) }),
      salaryDisplayLabel ? /* @__PURE__ */ jsx(Box, { "aria-label": `Salary: ${salaryDisplayLabel}`, children: /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "jobSalary" }, maxLines: 2, children: salaryDisplayLabel }) }) : null,
      !(featureFlags == null ? void 0 : featureFlags.newJobCardDensity) && (classificationLinks == null ? void 0 : classificationLinks.length) ? /* @__PURE__ */ jsx(JobClassifications, {}) : null
    ] })
  ] });
};
const JobCard = forwardRef(
  (props, refOriginal) => {
    const ref = useRef(null);
    return /* @__PURE__ */ jsx(JobCardContextProvider, { ...props, children: /* @__PURE__ */ jsx(JobCardBase, { ...props, ref: refOriginal ?? ref }) });
  }
);
export {
  JobCard,
  useJobCardContext
};
